import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import { getAuthHeaders } from '../utils/auth';
import '../assets/styles/SmartStore.css';

const SmartStore = ({ kidUserId, userRole = 'kid' }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState('all');
    const [syncing, setSyncing] = useState(false);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            let url = `/api/v1/store/products?category=${category === 'all' ? '' : category}`;
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }

            const response = await axios.get(url, {
                headers: getAuthHeaders()
            });
            // Backend returns { products: [], count: 0 }
            setProducts(response.data.products || []);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch products:', err);
            setError('Failed to load products');
        } finally {
            setLoading(false);
        }
    }, [category, searchTerm]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await axios.post('/api/v1/admin/store/sync', {}, {
                headers: getAuthHeaders()
            });
            alert('Store sync started successfully! Products will update soon.');
            fetchProducts();
        } catch (err) {
            console.error('Failed to sync store:', err);
            alert('Failed to sync store. Please check admin permissions.');
        } finally {
            setSyncing(false);
        }
    };

    const handleSetGoal = async (product) => {
        try {
            const targetId = kidUserId || 'me'; // If kidUserId is provided, use it, otherwise 'me'
            await axios.post('/api/v1/goals', {
                kid_user_id: targetId,
                title: product.name,
                target_amount: product.price_cents,
                image_url: product.image_url,
                amazon_url: product.affiliate_url
            }, {
                headers: getAuthHeaders()
            });
            alert(`"${product.name}" has been set as your active savings goal! 🎯`);
        } catch (err) {
            console.error('Failed to set goal:', err);
            alert('Failed to set goal');
        }
    };

    const handleTrackClick = async (product) => {
        try {
            await axios.post('/api/v1/store/track-click', {
                product_id: product.asin,
                product_name: product.name,
                category: product.category
            }, {
                headers: getAuthHeaders()
            });
        } catch (err) {
            console.warn('Click tracking failed', err);
        }
    };

    return (
        <div className="smart-store">
            <div className="store-header">
                <h2>🛍️ Smart Store</h2>
                <div className="store-controls">
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                        aria-label="Search products"
                    />
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="category-select" aria-label="Filter by category">
                        <option value="all">All Categories</option>
                        <option value="gaming">Gaming</option>
                        <option value="toys">Toys & Games</option>
                        <option value="creative">Creative</option>
                        <option value="learning">Learning</option>
                    </select>
                    {userRole === 'parent' && (
                        <button
                            className="btn-sync"
                            onClick={handleSync}
                            disabled={syncing}
                            aria-label={syncing ? 'Syncing products' : 'Sync products from store'}
                        >
                            {syncing ? '🔄 Syncing...' : '🔄 Sync Products'}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="store-loading">Loading catalog...</div>
            ) : error ? (
                <div className="store-error">{error}</div>
            ) : products.length === 0 ? (
                <div className="store-empty">
                    <p>No products found. {userRole === 'parent' ? 'Try syncing the store.' : 'Ask your parent to sync the store!'}</p>
                </div>
            ) : (
                <div className="products-grid">
                    {Array.isArray(products) && products.map(product => (
                        <div key={product.asin} className="product-card">
                            <div className="product-image">
                                <img src={product.image_url} alt={product.name} />
                                {product.is_featured && <span className="featured-badge">Featured</span>}
                            </div>
                            <div className="product-info">
                                <h3>{product.name}</h3>
                                <p className="product-desc">{product.description?.substring(0, 100)}...</p>
                                <div className="product-footer">
                                    <div className="product-price">
                                        <span className="price-cents">${(product.price_cents / 100).toFixed(2)}</span>
                                        <span className="db-cost">{Math.ceil(product.price_cents / 100)} DB</span>
                                    </div>
                                    <div className="product-actions">
                                        <button
                                            className="btn-goal"
                                            onClick={() => handleSetGoal(product)}
                                            title="Set as Savings Goal"
                                            aria-label={`Set ${product.name} as savings goal`}
                                        >
                                            🎯 Goal
                                        </button>
                                        <a
                                            href={product.affiliate_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-view"
                                            onClick={() => handleTrackClick(product)}
                                            aria-label={`View ${product.name} on Amazon`}
                                        >
                                            View ↗
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SmartStore;
